import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router";
import Label from "../form/Label";
import Input from "../form/input/InputField";
import { authService } from "../../services/authService";

export default function ResetPasswordForm() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(() => searchParams.get("email")?.trim() ?? "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
  const confirmationHeadingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (submittedEmail) {
      confirmationHeadingRef.current?.focus();
    }
  }, [submittedEmail]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const normalizedEmail = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      setError("Enter a valid email address.");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await authService.forgotPassword(normalizedEmail);
      setSubmittedEmail(normalizedEmail);
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : "Unable to request a reset link. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col flex-1 w-full lg:w-1/2">
      <div className="w-full max-w-md pt-10 mx-auto">
        <Link
          to="/signin"
          className="inline-flex items-center text-sm text-gray-500 transition-colors hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
        >
          <svg
            className="stroke-current"
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              d="M12.7083 5L7.5 10.2083L12.7083 15.4167"
              stroke=""
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Back to sign in
        </Link>
      </div>

      <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto">
        {submittedEmail ? (
          <div
            role="status"
            aria-live="polite"
            aria-atomic="true"
            className="rounded-2xl border border-success-200 bg-success-50 p-6 shadow-theme-sm dark:border-success-800 dark:bg-success-900/20 sm:p-8"
          >
            <div
              aria-hidden="true"
              className="mb-5 flex size-12 items-center justify-center rounded-full bg-success-100 text-success-700 dark:bg-success-800/40 dark:text-success-300"
            >
              <svg viewBox="0 0 24 24" fill="none" className="size-7" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M7 12.5L10.25 15.75L17.5 8.5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h1
              ref={confirmationHeadingRef}
              tabIndex={-1}
              className="mb-3 text-title-sm font-semibold text-gray-800 outline-none dark:text-white/90 sm:text-title-md"
            >
              Request received
            </h1>
            <p className="text-sm leading-6 text-gray-600 dark:text-gray-300">
              If an active account matches{" "}
              <span className="font-medium text-gray-800 dark:text-white/90">{submittedEmail}</span>,
              you’ll receive a password reset link. Delivery can take a few minutes, so check your spam or junk folder too.
            </p>
            <div className="mt-6 space-y-3">
              <Link
                to="/signin"
                className="flex w-full items-center justify-center rounded-lg bg-brand-500 px-4 py-3 text-sm font-medium text-white shadow-theme-xs transition hover:bg-brand-600"
              >
                Back to sign in
              </Link>
              <button
                type="button"
                onClick={() => {
                  setSubmittedEmail(null);
                  setError(null);
                  window.requestAnimationFrame(() => {
                    const emailInput = document.getElementById("email");
                    if (emailInput instanceof HTMLInputElement) {
                      emailInput.focus();
                      emailInput.select();
                    }
                  });
                }}
                className="flex w-full items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-white/[0.03]"
              >
                Try another email
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-5 sm:mb-8">
              <h1 className="mb-2 font-semibold text-gray-800 text-title-sm dark:text-white/90 sm:text-title-md">
                Forgot Your Password?
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Enter the email address linked to your account, and we’ll send you a link to reset your password.
              </p>
            </div>
            <div>
              <form onSubmit={handleSubmit} noValidate aria-busy={isSubmitting}>
                <div className="space-y-5">
                  <div>
                    <Label htmlFor="email">
                      Email<span className="text-error-500">*</span>
                    </Label>
                    <Input
                      type="email"
                      id="email"
                      name="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(event) => {
                        setEmail(event.target.value);
                        setError(null);
                      }}
                      disabled={isSubmitting}
                      required
                      autoComplete="email"
                      error={Boolean(error)}
                      aria-invalid={Boolean(error)}
                      aria-describedby={error ? "forgot-password-error" : undefined}
                    />
                  </div>

                  {error && (
                    <p id="forgot-password-error" role="alert" className="text-sm text-error-600 dark:text-error-400">
                      {error}
                    </p>
                  )}

                  <div>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex items-center justify-center w-full px-4 py-3 text-sm font-medium text-white transition rounded-lg bg-brand-500 shadow-theme-xs hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {isSubmitting ? "Sending…" : "Send Reset Link"}
                    </button>
                  </div>
                </div>
              </form>
              <div className="mt-5">
                <p className="text-sm font-normal text-center text-gray-700 dark:text-gray-400 sm:text-start">
                  Wait, I remember my password...{" "}
                  <Link
                    to="/signin"
                    className="text-brand-500 hover:text-brand-600 dark:text-brand-400"
                  >
                    Click here
                  </Link>
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
